import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";

import { PartnerDetailContent } from "./PartnerDetailContent";
import { PartnerQuickInfo } from "./PartnerQuickInfo";

/**
 * Drawer for a partner record. Mounted once inside PartnersPage; visibility
 * is fully derived from the route's search params:
 *
 *   ?partner=<accountId>             → quick view drawer
 *   ?partner=<accountId>&expanded=1  → expanded drawer (full detail)
 *   no `partner`                     → drawer closed
 *
 * The roster stays mounted behind both modes, so closing returns to the same
 * scroll position and filters. Mirrors renewals/CustomerDrawer.
 */
export function PartnerDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/partners" });
  const accountId = search.partner ?? null;
  const expanded = !!search.expanded;
  const open = accountId != null;

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) {
          navigate({ to: "/partners", search: {} });
        }
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (accountId == null) return;
        navigate({
          to: "/partners",
          search: exp
            ? { partner: accountId, expanded: true }
            : { partner: accountId },
        });
      }}
      onClose={() => {
        if (accountId == null) return;
        // Peel: expanded → quick → closed.
        if (expanded) {
          navigate({ to: "/partners", search: { partner: accountId } });
        } else {
          navigate({ to: "/partners", search: {} });
        }
      }}
      title="Partner"
      subtitle={accountId != null ? accountId.slice(0, 8) : ""}
    >
      {accountId != null && (
        expanded
          ? <PartnerDetailContent accountId={accountId} />
          : <PartnerQuickInfo accountId={accountId} />
      )}
    </EntityDrawer>
  );
}
